




































import { Component, Vue, Watch } from 'vue-property-decorator';
import * as THREE from 'three';
import { GLTFLoader } from '@/three/loaders/GLTFLoader.js';
import { DRACOLoader } from '@/three/loaders/DRACOLoader.js';
import { PointerLockControls } from '@/three/controls/PointerLockControls.js';
import { RoomEnvironment } from '@/three/environments/RoomEnvironment.js';
import GltfPointKeymap from '@/views/vr/GLTFPointKeymap.vue';

// GLTF模型预览(锁定光标，并通过键盘控制来移动位置)
@Component({
  components: { GltfPointKeymap },
})
export default class GltfPointPreview extends Vue {
  public MAX_MULTIPLIER = 7;        // 移速最大挡位. 2的幂数
  public MIN_MULTIPLIER = -5;       // 移速最小挡位. 2的幂数
  public NORMAL_STEP = 1;           // 移速正常步长
  public PRECISE_STEP = 0.1;        // 移速微调步长

  public timer!: any;
  public gltfUri = '';              // gltf 文件路径
  public errMsg = '';               // gltf 加载出错时的信息
  public percent = 0;               // gltf 加载进度
  public loading = false;           // gltf 是否加载中
  public showKeyMap = true;         // 显示快捷提示
  public neverShowKeyMap = false;   // 永不显示快捷提示
  public prevTime = performance.now();

  public camera!: any;
  public renderer!: any;
  public scene!: any;
  public controls!: any;

  public moveForward = false;
  public moveBackward = false;
  public moveLeft = false;
  public moveRight = false;
  public moveUp = false;
  public moveDown = false;
  public speedUp = false;
  public speedDown = false;
  public speedMicroUp = false;
  public speedMicroDown = false;
  public speedStep = 0;       // 用于调整移速挡位

  public useRoomEnv = false;  // 是否使用默认环境光
  public envRoom!: any;       // 默认环境光源
  public envFrog = new THREE.Fog(0xffffff, 0, 750);

  public velocity = new THREE.Vector3();
  public direction = new THREE.Vector3();
  public vertex = new THREE.Vector3();
  public color = new THREE.Color();

  // 是否显示快捷键提示
  public get showKeymap() {
    return !this.neverShowKeyMap && this.showKeyMap && this.gltfUri;
  }

  // 是否显示提示框
  public get showMsgBox() {
    const isInPress = this.speedUp || this.speedDown || this.speedMicroUp || this.speedMicroDown;
    return !this.loading && isInPress;
  }

  // 移速信息
  public get speedMsg() {
    return '移速：' + (this.speedStep + 6).toFixed(1);
  }

  public mounted() {
    this.gltfUri = (this.$route.query.url || '') as string;
    if (this.gltfUri) {
      this.init();
      this.animate();
    }
  }

  public init() {
    const { container, ctWidth, ctHeight } = this.getHtmlCt();

    // 透视投影相机
    // 如果希望渲染的结果符合人眼的远小近大的规律，选择透视投影相机 PerspectiveCamera
    // 如果不需要模拟人眼远小近大的投影规律，可以选择正投影相机 OrthographicCamera
    const fov = 45;   // 拍摄距离
    const far = 1000; // 最大范围
    const near = 1;   // 最小范围
    this.camera = new THREE.PerspectiveCamera(fov, ctWidth / ctHeight, near, far);
    this.camera.position.set(0, 0, 0);

    // 渲染器
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(ctWidth, ctHeight);
    container.appendChild(this.renderer.domElement);

    // 场景
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf0f0f0);

    // 环境光
    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    const roomEnv = new RoomEnvironment(this.renderer);
    this.envRoom = pmremGenerator.fromScene(roomEnv, 0.04).texture;

    // 控制器
    this.createControl(container);

    // 加载器
    this.loaderGltf();

    document.addEventListener( 'keydown', this.keydownHandler);
    document.addEventListener( 'keyup', this.keyUpHandler);
    window.addEventListener('resize', this.onWindowResize);
  }

  // 渲染
  public animate() {
    requestAnimationFrame( this.animate );

    const time = performance.now();

    // 如果加上这个 if ，则只有锁定光标模式才能移动
    // if ( this.controls.isLocked === false ) {
    const speedConst = 50.0;
    const step = Math.pow(2, this.speedStep);
    const delta = ( time - this.prevTime ) / 1000;

    this.velocity.x -= this.velocity.x * 10.0 * delta;
    this.velocity.z -= this.velocity.z * 10.0 * delta;
    this.velocity.y -= this.velocity.y * 10.0 * delta;

    // 计算正向还是负向移动
    this.direction.z = Number(this.moveForward) - Number(this.moveBackward);
    this.direction.x = Number(this.moveRight) - Number(this.moveLeft);
    this.direction.y = Number(this.moveUp) - Number(this.moveDown);
    this.direction.normalize(); // 这确保了在所有方向上的一致运动

    // 计算前进或后退的距离
    if ( this.moveForward || this.moveBackward ) {
      this.velocity.z -= this.direction.z * speedConst * delta * step;
    }

    // 计算向左或向右的距离
    if ( this.moveLeft || this.moveRight ) {
      this.velocity.x -= this.direction.x * speedConst * delta * step;
    }

    // 计算向上或向下的距离
    if ( this.moveUp || this.moveDown ) {
      this.velocity.y -= this.direction.y * speedConst * delta * step;
    }

    this.controls.moveRight(-this.velocity.x * delta);
    this.controls.moveForward(-this.velocity.z * delta);
    this.controls.moveUp(-this.velocity.y * delta);
    // }

    this.prevTime = time;
    this.renderer.render(this.scene, this.camera);
  }

  // 创建控制器
  public createControl(container: HTMLElement) {
    this.controls = new PointerLockControls(this.camera, container);
    this.scene.add(this.controls.getObject());
    const domEl = this.controls.domElement;

    // 屏蔽鼠标右键的菜单
    domEl.addEventListener('contextmenu', (e: KeyboardEvent) => {
      e.preventDefault();
    });

    // 鼠标右键按下时，进入锁定模式(隐藏光标)
    domEl.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button === 2) {
        this.controls.lock();
      }
    });

    // 鼠标右键弹起时，取消锁定模式(显示光标)
    domEl.addEventListener('mouseup', (e: MouseEvent) => {
      if (e.button === 2) {
        this.controls.unlock();
      }
    });

    // 鼠标滚轮时，缩放相机焦点
    domEl.addEventListener('mousewheel', (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.controls.scale(e);
    }, false);

    // 锁定视角时，隐藏键位提示
    this.controls.addEventListener('lock', () => {
      this.showKeyMap = false;
    });

    // 解锁视角时，显示键位提示
    this.controls.addEventListener('unlock', () => {
      this.showKeyMap = true;
    });
  }

  // 加载模型
  public loaderGltf() {
    this.loading = true;
    this.errMsg = '';
    this.percent = 0;

    // 解压缩
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/draco/');  // 指定包含 WASM/JS 解码库的文件夹的路径. 源码在 public 目录下
    dracoLoader.preload();

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);
    loader.load(this.gltfUri, (gltf: any) => {
      // 加载完成
      this.scene.add(gltf.scene);
      this.loading = false;

      // console.info("[glTF Viewer] Debugging data：");
      // this.printGraph(this.scene);
    }, (xhr: any) => {
      // 加载中
      this.loading = true;
      this.percent = Math.floor(xhr.loaded / xhr.total * 100);
    }, (err: any) => {
      // 加载出错
      this.loading = true;
      this.percent = 0;
      this.errMsg = `加载出错：${err.message}`;
      throw new Error(err);
    });
  }

  // 画布跟随窗口变化
  public onWindowResize() {
    const { ctWidth, ctHeight } = this.getHtmlCt();
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(ctWidth, ctHeight);
  }

  // 键盘按下时的事件处理
  public keydownHandler(event: KeyboardEvent) {
    if (this.loading) {
      return;
    }
    if (event.key === 'w') {
      this.moveForward = true;
    } else if (event.key === 'a') {
      this.moveLeft = true;
    } else if (event.key === 's') {
      this.moveBackward = true;
    } else if (event.key === 'd') {
      this.moveRight = true;
    } else if (event.key === 'e') {
      this.moveUp = true;
    } else if (event.key === 'c') {
      this.moveDown = true;
    } else if ((event.altKey && event.key === '+') || (event.altKey && event.key === '=')) {
      clearTimeout(this.timer);
      this.speedMicroUp = true;
      this.speedStep = Math.min(this.speedStep + this.PRECISE_STEP, this.MAX_MULTIPLIER);
    } else if (event.altKey && event.key === '-') {
      clearTimeout(this.timer);
      this.speedMicroDown = true;
      this.speedStep = Math.max(this.speedStep - this.PRECISE_STEP, this.MIN_MULTIPLIER);
    } else if (event.key === '+' || event.key === '=') {
      clearTimeout(this.timer);
      this.speedUp = true;
      this.speedStep = Math.min(this.speedStep + this.NORMAL_STEP, this.MAX_MULTIPLIER);
    } else if (event.key === '-') {
      clearTimeout(this.timer);
      this.speedDown = true;
      this.speedStep = Math.max(this.speedStep - this.NORMAL_STEP, this.MIN_MULTIPLIER);
    }
  }

  // 键盘弹起时的事件处理
  public keyUpHandler(event: KeyboardEvent) {
    if (event.key === 'w') {
      this.moveForward = false;
    } else if (event.key === 'a') {
      this.moveLeft = false;
    } else if (event.key === 's') {
      this.moveBackward = false;
    } else if (event.key === 'd') {
      this.moveRight = false;
    } else if (event.key === 'e') {
      this.moveUp = false;
    } else if (event.key === 'c') {
      this.moveDown = false;
    } else if (event.altKey || event.key === '=' || event.key === '+' || event.key === '-') {
      this.clearTimerStatus();
    }
  }

  // 关闭延迟状态
  public clearTimerStatus() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.speedUp = false;
      this.speedDown = false;
      this.speedMicroUp = false;
      this.speedMicroDown = false;
      this.timer = null;
    }, 600);
  }

  // 获取 html 容器信息
  public getHtmlCt(domSelector = '#container') {
    const container = document.querySelector(domSelector) as HTMLElement;
    const ctWidth = (container.parentElement as HTMLElement).clientWidth;
    const ctHeight = (container.parentElement as HTMLElement).clientHeight;
    return { container, ctWidth, ctHeight };
  }

  // 打印 debug 信息
  // public printGraph(e: any) {
  //   console.group(' <' + e.type + '> ' + e.name);
  //   e.children.forEach((r: any) => this.printGraph(r));
  //   console.groupEnd();
  // }

  // 设置或取消设置环境光
  @Watch('useRoomEnv')
  public useRoomEnvChange(useRoomEnv: boolean) {
    if (useRoomEnv) {
      this.scene.fog = this.envFrog;
      this.scene.environment = this.envRoom;
    } else {
      this.scene.fog = null;
      this.scene.environment = null;
    }
  }
}
